import { toast } from "sonner";

/**
 * What the app says after it has done something.
 *
 * One vocabulary, so a message's level is a choice rather than an accident of which
 * `toast.*` a call site happened to reach for. Only `success` and `error` were in use
 * before, which meant anything that was neither — a save that applied but left
 * something unresolved, or a note about what will happen next — had to pick one of
 * the two and be slightly wrong.
 *
 * Toasts report; they do not ask. Anything the user has to agree to before it happens
 * goes through `useConfirm` (see `@/components/confirm-dialog`), because a toast is
 * dismissible, non-blocking and easy to miss — which is exactly wrong for a question
 * whose answer deletes something.
 *
 * The four levels:
 *
 *  - `success` — it happened. Say what, in the past tense.
 *  - `error`   — it did not happen, and nothing changed.
 *  - `warning` — it happened, but something about the result needs attention.
 *  - `info`    — nothing happened; this is context the user asked for or needs next.
 */

/** The optional second line, for the detail that does not belong in the headline. */
export interface NotifyDetail {
  description?: string;
  /** Milliseconds. Omitted uses Sonner's default; `Infinity` waits to be dismissed. */
  duration?: number;
}

/**
 * The readable message inside whatever a `catch` caught.
 *
 * Call sites wrote `err instanceof Error ? err.message : "Something went wrong"` over
 * and over, and the ones that forgot rendered `[object Object]` into a toast. Also
 * unwraps the API client's `{ message }` error shape.
 */
export function messageOf(error: unknown, fallback = "Something went wrong"): string {
  if (typeof error === "string") return error || fallback;
  if (error instanceof Error) return error.message || fallback;
  if (error && typeof error === "object" && "message" in error) {
    const message = (error as { message?: unknown }).message;
    if (typeof message === "string" && message) return message;
  }
  return fallback;
}

export const notify = {
  /** It happened. */
  success(message: string, detail?: NotifyDetail) {
    return toast.success(message, detail);
  },

  /**
   * It did not happen.
   *
   * Takes whatever a `catch` caught as well as a plain string, so a call site does not
   * have to narrow the error itself before it can report it.
   */
  error(error: unknown, detail?: NotifyDetail) {
    return toast.error(messageOf(error), detail);
  },

  /** It happened, but the result needs attention. */
  warning(message: string, detail?: NotifyDetail) {
    return toast.warning(message, detail);
  },

  /** Context, not an outcome. */
  info(message: string, detail?: NotifyDetail) {
    return toast.info(message, detail);
  },

  /**
   * In progress. Returns the toast's id — hand it back to `success`, `error` or
   * `dismiss` through `detail.id` so the one toast changes rather than stacking up.
   */
  loading(message: string, detail?: NotifyDetail) {
    return toast.loading(message, detail);
  },

  dismiss(id?: string | number) {
    toast.dismiss(id);
  },

  /**
   * A toast that follows a promise from pending to settled, in place.
   *
   * For work long enough that silence reads as nothing happening — a bulk write, a
   * PDF being built — where a success toast afterwards would leave the wait unexplained.
   */
  promise<T>(
    work: Promise<T>,
    messages: { loading: string; success: string | ((value: T) => string); error?: string },
  ) {
    return toast.promise(work, {
      loading: messages.loading,
      success: messages.success,
      error: (error: unknown) => messageOf(error, messages.error),
    });
  },
};
